/**
 * ADR 0040 named, schema-validated tools: the registry vocabulary the first worker may draw from. A tool here is a
 * declaration (id, plain-language label for the customer card, JSON Schema the model is shown, where it executes);
 * naming a tool grants nothing. The broker enforces the connection and operation grant per credential independently,
 * and the API enforces the thread / attempt binding on every agent route. Handlers live in `apps/worker`; the API reads
 * this registry to validate a manifest's `tool_ids` against the agent's ceiling and to render the provisioning card.
 *
 * Prior art (PRD-025 §5a): Anthropic / OpenAI tool-use declarations (name + JSON Schema input, the model emits a
 * structured call, the caller validates and executes) and MCP's tool listing (adopted as the declaration format;
 * dynamic discovery rejected: `tool_ids` is fixed per manifest).
 */

export type ToolId
  = | 'bundle.read'
    | 'agent.message'
    | 'question.ask'
    | 'review.propose'
    | 'result.submit'
    | 'qbo.uncategorised_transactions.read'
    | 'qbo.bank_balances.read'
    | 'qbo.bank_register.read'
    | 'attachment.statement.read'
    | 'attachment.cash_count.read'
    | 'attachment.pos_closing_report.read'
    | 'decimal.sum'
    | 'decimal.multiply_round'

/** Where the call executes: the Gallop API through the broker relay, a narrow broker QBO operation, or a pinned local handler with no network. */
export type ToolSurface = 'api' | 'broker' | 'local'

export interface JsonSchema {
  readonly type: 'object'
  readonly properties: Readonly<Record<string, unknown>>
  readonly required?: ReadonlyArray<string>
  readonly additionalProperties?: false
}

export interface ToolDefinition {
  readonly id: ToolId
  /** Plain-language label on the customer's provisioning card (§3.3 "What the card shows to the firm"). */
  readonly label: string
  /** The description the model reads. Never a prompt for the customer. */
  readonly description: string
  readonly surface: ToolSurface
  readonly schemaVersion: 1
  readonly input: JsonSchema
}

const TOOLS: ReadonlyArray<ToolDefinition> = [
  {
    id: 'bundle.read',
    label: 'Read its instructions',
    description: 'Read one declared artifact of the pinned bundle: the skill instructions, the Logic docs, or the integration notes.',
    surface: 'api',
    schemaVersion: 1,
    input: { type: 'object', properties: { artifact: { type: 'string', description: 'Artifact name from the bundle index, e.g. "skill" or "logic".' } }, required: ['artifact'], additionalProperties: false },
  },
  {
    id: 'agent.message',
    label: 'Post a short progress note',
    description: 'Post one plain-language progress line (≤ 500 chars) the people on the file will read in the thread. No data values, no names.',
    surface: 'api',
    schemaVersion: 1,
    input: { type: 'object', properties: { text: { type: 'string', maxLength: 500 } }, required: ['text'], additionalProperties: false },
  },
  {
    id: 'question.ask',
    label: 'Ask the person a question',
    description: 'Ask the responsible person one typed question and wait for the answer. Only one question may be open at a time. Use choose_one with options when the answer is one of a known set.',
    surface: 'api',
    schemaVersion: 1,
    input: {
      type: 'object',
      properties: {
        questionKind: { type: 'string', enum: ['choose_one', 'confirm', 'provide_value', 'free_text'] },
        prompt: { type: 'string', maxLength: 1000 },
        options: { type: 'array', items: { type: 'object', properties: { value: { type: 'string' }, label: { type: 'string' } }, required: ['value', 'label'] } },
        valueType: { type: 'string', enum: ['text', 'decimal', 'date'] },
        reasonStep: { type: 'string', maxLength: 120, description: 'Which step of the skill needs the answer.' },
      },
      required: ['questionKind', 'prompt', 'reasonStep'],
      additionalProperties: false,
    },
  },
  {
    id: 'review.propose',
    label: 'Propose a change for approval',
    description: 'Propose one write to the books as a Review card. It never posts by itself: a person approves, edits or rejects it. Cite at least one Logic rule and one source row.',
    surface: 'api',
    schemaVersion: 1,
    input: {
      type: 'object',
      properties: {
        writeType: { type: 'string', enum: ['reclass', 'adjustment', 'categorisation', 'accrual', 'reversal'] },
        proposed: {
          type: 'object',
          properties: {
            v: { type: 'integer', enum: [1] },
            target: { type: 'object', properties: { account_tokens: { type: 'array', items: { type: 'string' } }, row_ids: { type: 'array', items: { type: 'string' } } }, required: ['account_tokens', 'row_ids'] },
            before: { type: 'object', properties: { lines: { type: 'array', items: { type: 'object', properties: { account_token: { type: 'string' }, amount: { type: 'string' }, memo: { type: 'string' } }, required: ['account_token', 'amount'] } } }, required: ['lines'] },
            after: { type: 'object', properties: { lines: { type: 'array', items: { type: 'object', properties: { account_token: { type: 'string' }, amount: { type: 'string' }, memo: { type: 'string' } }, required: ['account_token', 'amount'] } } }, required: ['lines'] },
            memo: { type: 'string', maxLength: 300 },
            rationale: { type: 'string', maxLength: 1000 },
          },
          required: ['v', 'target', 'before', 'after', 'memo', 'rationale'],
        },
        evidence: { type: 'object', properties: { v: { type: 'integer', enum: [1] }, citations: { type: 'array', items: { type: 'object', properties: { kind: { type: 'string', enum: ['logic', 'source_row', 'snapshot', 'attachment', 'answer'] }, ref: { type: 'string' }, quote: { type: 'string' } }, required: ['kind', 'ref'] } } }, required: ['v', 'citations'] },
        idempotencyKey: { type: 'string', maxLength: 200, description: 'Stable per proposal, e.g. the source row id.' },
      },
      required: ['writeType', 'proposed', 'evidence', 'idempotencyKey'],
      additionalProperties: false,
    },
  },
  {
    id: 'result.submit',
    label: 'Submit a result',
    description: 'Submit the result card: a summary, the figures as decimal strings, and the evidence citations. This ends your work on the attempt.',
    surface: 'api',
    schemaVersion: 1,
    input: {
      type: 'object',
      properties: {
        summary: { type: 'string', maxLength: 2000 },
        figures: { type: 'array', items: { type: 'object', properties: { label: { type: 'string' }, value: { type: 'string' } }, required: ['label', 'value'] } },
        evidence: { type: 'array', items: { type: 'object', properties: { kind: { type: 'string' }, ref: { type: 'string' } }, required: ['kind', 'ref'] } },
      },
      required: ['summary', 'figures', 'evidence'],
      additionalProperties: false,
    },
  },
  {
    id: 'qbo.uncategorised_transactions.read',
    label: 'Read uncategorized transactions from QuickBooks',
    description: 'Read the period\'s transactions whose lines sit in an Uncategorized account. Rows come back tokenized: row id, date, amount, account token, vendor token. No names.',
    surface: 'broker',
    schemaVersion: 1,
    input: { type: 'object', properties: { startDate: { type: 'string', pattern: '^\\d{4}-\\d{2}-\\d{2}$' }, endDate: { type: 'string', pattern: '^\\d{4}-\\d{2}-\\d{2}$' } }, required: ['startDate', 'endDate'], additionalProperties: false },
  },
  {
    id: 'qbo.bank_balances.read',
    label: 'Read bank balances from QuickBooks',
    description: 'Read the bank account balances as of a date (the fixture operation).',
    surface: 'broker',
    schemaVersion: 1,
    input: { type: 'object', properties: { asOf: { type: 'string', pattern: '^\\d{4}-\\d{2}-\\d{2}$' } }, required: ['asOf'], additionalProperties: false },
  },
  {
    id: 'decimal.sum',
    label: 'Add amounts',
    description: 'Add decimal-string amounts exactly. Use this instead of arithmetic in your head.',
    surface: 'local',
    schemaVersion: 1,
    input: { type: 'object', properties: { amounts: { type: 'array', items: { type: 'string' } } }, required: ['amounts'], additionalProperties: false },
  },
  {
    id: 'decimal.multiply_round',
    label: 'Multiply and round to the cent',
    description: 'Multiply two decimal strings exactly and round half-up to two places (a rate applied to a base). Use this instead of arithmetic in your head.',
    surface: 'local',
    schemaVersion: 1,
    input: { type: 'object', properties: { amount: { type: 'string', pattern: '^-?\\d+(?:\\.\\d+)?$' }, factor: { type: 'string', pattern: '^-?\\d+(?:\\.\\d+)?$' } }, required: ['amount', 'factor'], additionalProperties: false },
  },
  {
    id: 'qbo.bank_register.read',
    label: 'Read the bank register from QuickBooks',
    description: 'Read every register row of one bank account for the period (purchases, deposits, transfers, bill payments, payments, journal lines touching it) with the book opening and ending balances. Rows come back tokenized: row id, date, signed amount (deposit positive), payee token. No names.',
    surface: 'broker',
    schemaVersion: 1,
    input: { type: 'object', properties: { accountToken: { type: 'string', pattern: '^acct:[\\w-]{1,40}$' }, startDate: { type: 'string', pattern: '^\\d{4}-\\d{2}-\\d{2}$' }, endDate: { type: 'string', pattern: '^\\d{4}-\\d{2}-\\d{2}$' } }, required: ['accountToken', 'startDate', 'endDate'], additionalProperties: false },
  },
  {
    id: 'attachment.statement.read',
    label: 'Read the bank statement attached to this item',
    description: 'Read the bank statement attached to the item (scanned clean) through its pinned schema: opening, closing, and lines {line_id, date, description, amount}. Anything outside the schema never reaches you.',
    surface: 'api',
    schemaVersion: 1,
    input: { type: 'object', properties: {}, additionalProperties: false },
  },
  {
    id: 'attachment.cash_count.read',
    label: 'Read the cash count attached to this item',
    description: 'Read the cash count attached to the item (scanned clean) through its pinned schema: as_of, counted_total, denominations. Absent means the file keeps no cash on hand for the period.',
    surface: 'api',
    schemaVersion: 1,
    input: { type: 'object', properties: {}, additionalProperties: false },
  },
  {
    id: 'attachment.pos_closing_report.read',
    label: 'Read the POS closing report attached to this item',
    description: 'Read the POS closing report attached to the item (scanned clean) through its pinned schema: location label, period from/to, cannabis sales, medical cannabis sales, lounge sales, the POS\'s own excise line. Nothing else reaches you.',
    surface: 'api',
    schemaVersion: 1,
    input: { type: 'object', properties: {}, additionalProperties: false },
  },
]

export const TOOL_REGISTRY: ReadonlyMap<ToolId, ToolDefinition> = new Map(TOOLS.map(t => [t.id, t]))

export function toolDefinition(id: string): ToolDefinition | null {
  return TOOL_REGISTRY.get(id as ToolId) ?? null
}

/** The tools every manifest carries regardless of skill: read the bundle, talk to the thread, finish. */
export const PLATFORM_BASE_TOOLS: ReadonlyArray<ToolId> = ['bundle.read', 'agent.message', 'result.submit']

/** ADR 0040 decision 3: the model-visible set equals `tool_ids`, and every id must be registered and within the agent's ceiling. */
export function toolIdsProblem(toolIds: ReadonlyArray<string>, ceiling: ReadonlyArray<string> | null): { rule: 'tools.unregistered' | 'tools.outside_ceiling', toolId: string } | null {
  for (const id of toolIds) {
    if (!TOOL_REGISTRY.has(id as ToolId))
      return { rule: 'tools.unregistered', toolId: id }
    if (ceiling !== null && !ceiling.includes(id))
      return { rule: 'tools.outside_ceiling', toolId: id }
  }
  return null
}

/** Exact decimal-string addition (two places), for the `decimal.sum` handler and the worker's own checks. No floats. */
export function sumDecimalStrings(amounts: ReadonlyArray<string>): string {
  let cents = 0n
  for (const a of amounts) {
    const m = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(a.trim())
    if (!m)
      throw new TypeError(`not a decimal string: ${a}`)
    const sign = m[1] === '-' ? -1n : 1n
    const whole = BigInt(m[2]!)
    const frac = BigInt((m[3] ?? '').padEnd(2, '0'))
    cents += sign * (whole * 100n + frac)
  }
  const neg = cents < 0n
  const abs = neg ? -cents : cents
  const whole = abs / 100n
  const frac = (abs % 100n).toString().padStart(2, '0')
  return `${neg ? '-' : ''}${whole}.${frac}`
}

/** Whole cents of a two-place decimal string as a bigint; throws on anything else. */
export function decimalCents(amount: string): bigint {
  const m = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(amount.trim())
  if (!m)
    throw new TypeError(`not a decimal string: ${amount}`)
  return (m[1] === '-' ? -1n : 1n) * (BigInt(m[2]!) * 100n + BigInt((m[3] ?? '').padEnd(2, '0')))
}

/** A bigint of cents back to a two-place decimal string. */
export function centsToDecimal(cents: bigint): string {
  const neg = cents < 0n
  const abs = neg ? -cents : cents
  return `${neg ? '-' : ''}${abs / 100n}.${(abs % 100n).toString().padStart(2, '0')}`
}

/**
 * Exact `amount × factor` rounded half-up to two places, for the `decimal.multiply_round` handler (a tax rate applied
 * to a base: `nv-excise-rates#row-l5`). Both operands may carry any number of decimal places; the product is formed in
 * integer arithmetic and rounded once, away from zero on a tie (half-up on the absolute value). No floats.
 */
export function multiplyRoundHalfUp(amount: string, factor: string): string {
  const parse = (s: string): { n: bigint, scale: number } => {
    const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(s.trim())
    if (!m)
      throw new TypeError(`not a decimal string: ${s}`)
    const frac = m[3] ?? ''
    return { n: (m[1] === '-' ? -1n : 1n) * BigInt(m[2]! + frac), scale: frac.length }
  }
  const a = parse(amount)
  const b = parse(factor)
  const product = a.n * b.n
  const scale = a.scale + b.scale
  // Bring to two places: divide by 10^(scale-2) with half-up on the remainder (or multiply when scale < 2).
  if (scale <= 2)
    return centsToDecimal(product * 10n ** BigInt(2 - scale))
  const div = 10n ** BigInt(scale - 2)
  const neg = product < 0n
  const abs = neg ? -product : product
  let cents = abs / div
  if ((abs % div) * 2n >= div)
    cents += 1n
  return centsToDecimal(neg ? -cents : cents)
}
